import React, { useState } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  keyframes,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { motion, AnimatePresence } from "framer-motion";
import { API_URL } from "../../api/apiConfig";

import Matrix5 from "../../img/logos/Matrix5.png";
import { useTranslation } from "react-i18next";

const MotionBox = motion(Box);

const floatA = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -24px) scale(1.05); }
  66% { transform: translate(-20px, 16px) scale(0.96); }
`;

const floatB = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  40% { transform: translate(-28px, 20px) scale(1.08); }
  70% { transform: translate(18px, -14px) scale(0.94); }
`;

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.12 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 380, damping: 28 },
  },
};

const LoginPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { t } = useTranslation();

  const accent = theme.palette.primary.main;
  const accentSoft = isDark
    ? theme.palette.secondary.main
    : "#5b8a9a";

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/auth/token`,
        { username, password },
        { headers: { "Content-Type": "application/json" } },
      );

      const { token, user } = response.data;

      localStorage.setItem("token", token);

      if (user?.id) {
        localStorage.setItem("userId", user.id);
      }

      navigate("/post-login");
    } catch (err) {
      console.error("LOGIN ERROR:", err.response?.data || err);
      setError(t("Login.invalidCredentials"));
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    width: "100%",
    "& .MuiInputBase-root": {
      backgroundColor: isDark
        ? alpha("#fff", 0.04)
        : alpha("#fff", 0.72),
      color: theme.palette.text.primary,
      minHeight: { xs: 52, sm: 54 },
      borderRadius: 2.5,
      backdropFilter: "blur(8px)",
      transition:
        "box-shadow 0.25s ease, border-color 0.25s ease, background-color 0.25s ease",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.divider, isDark ? 0.55 : 0.7),
      transition: "border-color 0.25s ease",
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(accent, 0.55),
    },
    "& .MuiInputBase-root.Mui-focused": {
      backgroundColor: isDark
        ? alpha(accent, 0.08)
        : alpha("#fff", 0.95),
      boxShadow: `0 0 0 3px ${alpha(accent, 0.22)}`,
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: accent,
      borderWidth: "1.5px",
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: accent,
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      fontSize: { xs: "1rem", sm: "1rem" },
    },
    "& .MuiInputAdornment-root": {
      color: alpha(theme.palette.text.secondary, 0.85),
    },
  };

  return (
    <Box
      component="form"
      onSubmit={handleLogin}
      sx={{
        position: "relative",
        minHeight: "100vh",
        "@supports (min-height: 100dvh)": {
          minHeight: "100dvh",
        },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1.25, sm: 2 },
        pt: {
          xs: "max(16px, env(safe-area-inset-top))",
          sm: "max(24px, env(safe-area-inset-top))",
        },
        pb: {
          xs: "max(16px, env(safe-area-inset-bottom))",
          sm: "max(24px, env(safe-area-inset-bottom))",
        },
        pl: "max(12px, env(safe-area-inset-left))",
        pr: "max(12px, env(safe-area-inset-right))",
        overflow: "hidden",
        boxSizing: "border-box",
        background: isDark
          ? `radial-gradient(1200px 600px at 15% 10%, ${alpha(accent, 0.22)} 0%, transparent 55%),
             radial-gradient(900px 500px at 90% 80%, ${alpha(accentSoft, 0.18)} 0%, transparent 50%),
             linear-gradient(160deg, #1a1d1f 0%, #25292b 45%, #1e2224 100%)`
          : `radial-gradient(1100px 560px at 12% 8%, ${alpha(accent, 0.28)} 0%, transparent 55%),
             radial-gradient(900px 480px at 92% 85%, ${alpha("#90b2b7", 0.35)} 0%, transparent 50%),
             linear-gradient(155deg, #f3faf9 0%, #eef4f6 42%, #f7f5fb 100%)`,
      }}
    >
      {/* Ambient orbs */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: { xs: 220, sm: 320 },
          height: { xs: 220, sm: 320 },
          borderRadius: "50%",
          top: { xs: "-8%", sm: "-6%" },
          left: { xs: "-10%", sm: "-4%" },
          background: `radial-gradient(circle, ${alpha(accent, isDark ? 0.45 : 0.35)} 0%, transparent 70%)`,
          filter: "blur(8px)",
          animation: `${floatA} 14s ease-in-out infinite`,
          pointerEvents: "none",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: { xs: 240, sm: 380 },
          height: { xs: 240, sm: 380 },
          borderRadius: "50%",
          bottom: { xs: "-12%", sm: "-8%" },
          right: { xs: "-14%", sm: "-6%" },
          background: `radial-gradient(circle, ${alpha(accentSoft, isDark ? 0.4 : 0.32)} 0%, transparent 70%)`,
          filter: "blur(10px)",
          animation: `${floatB} 18s ease-in-out infinite`,
          pointerEvents: "none",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          opacity: isDark ? 0.07 : 0.045,
          backgroundImage:
            "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
          backgroundSize: "22px 22px",
          color: theme.palette.text.primary,
          pointerEvents: "none",
        }}
      />

      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: { xs: "100%", sm: 440 },
          px: { xs: 0, sm: 2 },
        }}
      >
        <MotionBox
          initial={{ opacity: 0, y: 28, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          sx={{
            position: "relative",
            borderRadius: { xs: 3.5, sm: 4 },
            p: "1px",
            background: isDark
              ? `linear-gradient(135deg, ${alpha(accent, 0.55)}, ${alpha("#fff", 0.12)} 40%, ${alpha(accentSoft, 0.45)})`
              : `linear-gradient(135deg, ${alpha(accent, 0.7)}, ${alpha("#fff", 0.85)} 45%, ${alpha(accentSoft, 0.55)})`,
            backgroundSize: "200% 200%",
            animation: `${shimmer} 8s ease infinite`,
            boxShadow: isDark
              ? `0 24px 64px ${alpha("#000", 0.55)}, 0 0 0 1px ${alpha("#fff", 0.04)}`
              : `0 28px 64px ${alpha("#0f172a", 0.12)}, 0 8px 24px ${alpha(accent, 0.12)}`,
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
            },
          }}
        >
          <Box
            sx={{
              borderRadius: { xs: 3.4, sm: 3.9 },
              overflow: "hidden",
              backdropFilter: "blur(20px)",
              background: isDark
                ? `linear-gradient(165deg, ${alpha("#1e2224", 0.92)} 0%, ${alpha("#171a1c", 0.96)} 100%)`
                : `linear-gradient(165deg, ${alpha("#ffffff", 0.92)} 0%, ${alpha("#f7fbfa", 0.96)} 100%)`,
              px: { xs: 2.5, sm: 3.5 },
              py: { xs: 3, sm: 4 },
            }}
          >
            <MotionBox
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 1.75, sm: 2 },
              }}
            >
              <MotionBox
                variants={itemVariants}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-start" },
                  mb: 0.5,
                }}
              >
                <Box
                  component="img"
                  src={Matrix5}
                  alt="MATRIX"
                  sx={{
                    width: { xs: 128, sm: 148 },
                    height: "auto",
                    maxHeight: { xs: 42, sm: 48 },
                    objectFit: "contain",
                    display: "block",
                    filter: isDark
                      ? "drop-shadow(0 0 12px rgba(0,194,163,0.25))"
                      : "drop-shadow(0 4px 12px rgba(0,194,163,0.18))",
                  }}
                />
              </MotionBox>

              <MotionBox variants={itemVariants}>
                <Typography
                  variant="overline"
                  sx={{
                    color: accent,
                    letterSpacing: { xs: 1.2, sm: 1.6 },
                    display: "block",
                    fontSize: { xs: "0.68rem", sm: "0.72rem" },
                    fontWeight: 700,
                    lineHeight: 1.4,
                  }}
                >
                  {t("Login.heroOverline")}
                </Typography>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    mb: 0.75,
                    fontSize: { xs: "1.65rem", sm: "1.9rem", md: "2rem" },
                    wordBreak: "break-word",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {t("Login.heroTitle")}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "0.95rem" },
                    lineHeight: 1.55,
                    maxWidth: 360,
                  }}
                >
                  {t("Login.heroSubtitle")}
                </Typography>
              </MotionBox>

              <MotionBox
                variants={itemVariants}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 1.75, sm: 2 },
                  mt: { xs: 0.5, sm: 1 },
                }}
              >
                <TextField
                  fullWidth
                  label={t("Login.username")}
                  variant="outlined"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  name="username"
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    enterKeyHint: "next",
                    autoCapitalize: "none",
                    autoCorrect: "off",
                  }}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  label={t("Login.password")}
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  name="password"
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword
                              ? t("Login.hidePassword")
                              : t("Login.showPassword")
                          }
                          onClick={() => setShowPassword((v) => !v)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                          disabled={loading}
                          sx={{
                            color: alpha(theme.palette.text.secondary, 0.9),
                            transition: "color 0.2s ease, transform 0.2s ease",
                            "&:hover": {
                              color: accent,
                              transform: "scale(1.08)",
                            },
                          }}
                        >
                          {showPassword ? (
                            <VisibilityOffOutlinedIcon fontSize="small" />
                          ) : (
                            <VisibilityOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ enterKeyHint: "go" }}
                  sx={fieldSx}
                />
              </MotionBox>

              <AnimatePresence>
                {error && (
                  <MotionBox
                    key="login-error"
                    initial={{ opacity: 0, height: 0, y: -6 }}
                    animate={{ opacity: 1, height: "auto", y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.22 }}
                  >
                    <Alert
                      severity="error"
                      sx={{
                        py: 0.5,
                        borderRadius: 2,
                        alignItems: "flex-start",
                        "& .MuiAlert-message": {
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  </MotionBox>
                )}
              </AnimatePresence>

              <MotionBox variants={itemVariants}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || !username || !password}
                  startIcon={
                    loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <LoginIcon />
                    )
                  }
                  sx={{
                    mt: { xs: 0.5, sm: 1 },
                    py: { xs: 1.75, sm: 1.6 },
                    minHeight: { xs: 50, sm: 48 },
                    fontWeight: 800,
                    borderRadius: 999,
                    textTransform: "none",
                    fontSize: { xs: "1rem", sm: "1.02rem" },
                    letterSpacing: 0.2,
                    color: "#fff",
                    background: `linear-gradient(120deg, ${accent} 0%, ${isDark ? "#5fd4c0" : "#00a88c"} 50%, ${accent} 100%)`,
                    backgroundSize: "200% 200%",
                    boxShadow: `0 10px 28px ${alpha(accent, 0.38)}`,
                    transition:
                      "transform 0.2s ease, box-shadow 0.2s ease, background-position 0.4s ease, opacity 0.2s ease",
                    "&:hover": {
                      backgroundPosition: "100% 50%",
                      boxShadow: `0 14px 36px ${alpha(accent, 0.5)}`,
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&.Mui-disabled": {
                      color: alpha("#fff", 0.7),
                      background: alpha(accent, 0.45),
                      boxShadow: "none",
                    },
                  }}
                >
                  {t("Login.loginButton")}
                </Button>
              </MotionBox>
            </MotionBox>
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default LoginPage;
